import { useMutation, useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import api, { queryClient } from '../api';
import type { ApiErrorResponse, ApiResponse } from '../types';
import { organisationResponseMapper } from './mappers';
import { apiDataResponseMapper, apiPayloadMapper } from '../utils';
import type {
  CreateOrganisationLabelPayload,
  GetOrganisationLabelPayload,
  GetOrganisationLabelsPayload,
  Organisation,
  OrganisationLabel,
  OrganisationLabelListResponse,
  OrganisationResponseData,
  OrgDomainsData,
  OrgDomainsResponseData,
  SSOEnforcementConfig,
  SSOEnforcementResponseData,
  UpdateOrganisationLabelPayload,
} from './type';

const ORGANISATION_LABELS_QUERY_KEY = 'organisation-labels';
const ORGANISATION_LABEL_QUERY_KEY = 'organisation-label';

const normalizeLabelListResponse = (
  data: unknown,
): OrganisationLabelListResponse => {
  const mapped = apiDataResponseMapper<
    object,
    OrganisationLabelListResponse & {
      results?: OrganisationLabel[];
      count?: number;
    }
  >(data as object);

  if (Array.isArray(data)) {
    const labels = apiDataResponseMapper<object[], OrganisationLabel[]>(
      data as object[],
    );

    return { labels, total: labels.length };
  }

  if (mapped.labels) {
    return {
      labels: mapped.labels,
      total: mapped.total ?? mapped.labels.length,
    };
  }

  if (mapped.results) {
    return {
      labels: mapped.results,
      total: mapped.count ?? mapped.results.length,
    };
  }

  return { labels: [], total: 0 };
};

const invalidateOrganisationLabels = () => {
  queryClient.invalidateQueries({ queryKey: [ORGANISATION_LABELS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [ORGANISATION_LABEL_QUERY_KEY] });
};

// APIs.
export const getMyOrgRequest = async (): Promise<ApiResponse<Organisation>> => {
  const response = await api.get<
    OrganisationResponseData,
    ApiResponse<OrganisationResponseData>
  >(`/organisations/my-org/`);

  return {
    ...response,
    data: organisationResponseMapper(response.data),
  };
};

export const uploadLogoRequest = async (
  logo: File,
): Promise<ApiResponse<Organisation>> => {
  const payload = new FormData();
  payload.set('logo', logo, logo.name);
  return await api.post('/organisations/logo/', payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getSSOEnforcementRequest = async (): Promise<
  ApiResponse<SSOEnforcementConfig>
> => {
  const response = await api.get<
    SSOEnforcementResponseData,
    ApiResponse<SSOEnforcementResponseData>
  >('/organisations/settings/');
  return {
    ...response,
    data: apiDataResponseMapper<SSOEnforcementResponseData, SSOEnforcementConfig>(response.data),
  };
};

export const updateSSOEnforcementRequest = async (
  payload: SSOEnforcementConfig,
): Promise<ApiResponse<SSOEnforcementConfig>> => {
  const response = await api.patch<
    SSOEnforcementResponseData,
    ApiResponse<SSOEnforcementResponseData>
  >('/organisations/settings/', apiPayloadMapper<SSOEnforcementConfig, SSOEnforcementResponseData>(payload));
  return {
    ...response,
    data: apiDataResponseMapper<SSOEnforcementResponseData, SSOEnforcementConfig>(response.data),
  };
};

export const getOrgDomainsRequest = async (): Promise<
  ApiResponse<OrgDomainsData>
> => {
  const response = await api.get<
    OrgDomainsResponseData,
    ApiResponse<OrgDomainsResponseData>
  >('/organisations/domains/');
  return {
    ...response,
    data: apiDataResponseMapper<OrgDomainsResponseData, OrgDomainsData>(response.data),
  };
};

export const getOrganisationLabels = async (
  payload?: GetOrganisationLabelsPayload,
): Promise<ApiResponse<OrganisationLabelListResponse>> => {
  const params = !isNil(payload?.isActive)
    ? { is_active: payload.isActive }
    : {};

  const response = await api.get<
    unknown,
    ApiResponse<OrganisationLabelListResponse>
  >('/labels/', { params });

  response.data = normalizeLabelListResponse(response.data);

  return response;
};

export const getOrganisationLabel = async (
  payload: GetOrganisationLabelPayload,
): Promise<ApiResponse<OrganisationLabel>> => {
  const response = await api.get<
    OrganisationLabel,
    ApiResponse<OrganisationLabel>
  >(`/labels/${payload.id}/`);

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const createOrganisationLabel = async (
  payload: CreateOrganisationLabelPayload,
): Promise<ApiResponse<OrganisationLabel>> => {
  const mappedPayload = apiPayloadMapper(payload);

  const response = await api.post<
    CreateOrganisationLabelPayload,
    ApiResponse<OrganisationLabel>
  >('/labels/', mappedPayload);

  response.data = apiDataResponseMapper(response.data);

  return response;
};

export const updateOrganisationLabel = async (
  payload: UpdateOrganisationLabelPayload,
): Promise<ApiResponse<OrganisationLabel>> => {
  const { id, ...rest } = payload;
  const mappedPayload = apiPayloadMapper(rest);

  const response = await api.patch<
    Omit<UpdateOrganisationLabelPayload, 'id'>,
    ApiResponse<OrganisationLabel>
  >(`/labels/${id}/`, mappedPayload);

  response.data = apiDataResponseMapper(response.data);

  return response;
};

// Hooks.
export const useMyOrg = () =>
  useQuery<any, ApiErrorResponse, ApiResponse<Organisation>>({
    queryKey: ['organisation'],
    queryFn: getMyOrgRequest,
  });

export const useUploadLogo = () =>
  useMutation<ApiResponse<Organisation>, ApiErrorResponse, any>({
    mutationFn: uploadLogoRequest,
  });

export const useSSOEnforcement = (enabled = true) =>
  useQuery<ApiResponse<SSOEnforcementConfig>, ApiErrorResponse, ApiResponse<SSOEnforcementConfig>>({
    queryKey: ['sso-enforcement'],
    queryFn: getSSOEnforcementRequest,
    enabled,
  });

export const useUpdateSSOEnforcement = () =>
  useMutation<
    ApiResponse<SSOEnforcementConfig>,
    ApiErrorResponse,
    SSOEnforcementConfig
  >({
    mutationFn: updateSSOEnforcementRequest,
  });

export const useOrgDomains = (enabled = true) =>
  useQuery<ApiResponse<OrgDomainsData>, ApiErrorResponse, ApiResponse<OrgDomainsData>>({
    queryKey: ['org-domains'],
    queryFn: getOrgDomainsRequest,
    enabled,
  });

export const useOrganisationLabels = (
  payload?: GetOrganisationLabelsPayload,
  enabled = true,
) =>
  useQuery<ApiResponse<OrganisationLabelListResponse>, ApiErrorResponse>({
    queryKey: [ORGANISATION_LABELS_QUERY_KEY, payload],
    queryFn: () => getOrganisationLabels(payload),
    enabled,
  });

export const useOrganisationLabel = (
  payload: GetOrganisationLabelPayload,
  enabled = true,
) =>
  useQuery<ApiResponse<OrganisationLabel>, ApiErrorResponse>({
    queryKey: [ORGANISATION_LABEL_QUERY_KEY, payload.id],
    queryFn: () => getOrganisationLabel(payload),
    enabled: enabled && !!payload.id,
  });

export const useCreateOrganisationLabel = () =>
  useMutation<
    ApiResponse<OrganisationLabel>,
    ApiErrorResponse,
    CreateOrganisationLabelPayload
  >({
    mutationFn: createOrganisationLabel,
    onSuccess: invalidateOrganisationLabels,
  });

export const useUpdateOrganisationLabel = () =>
  useMutation<
    ApiResponse<OrganisationLabel>,
    ApiErrorResponse,
    UpdateOrganisationLabelPayload
  >({
    mutationFn: updateOrganisationLabel,
    onSuccess: invalidateOrganisationLabels,
  });
